import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import useUrlTableState from '../../../hooks/useUrlTableState';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  User,
  Search,
  Download,
  Eye,
  Edit,
  Send,
  RefreshCw,
  Plus,
  X,
  Shield,
  Trash2,
  List
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import backgroundVerificationService from '../../../api/services/backgroundVerificationService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import { API_BASE_URL } from '../../../api/config';
import CustomAlert from '../../../components/common/CustomAlert';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import WarningPopup from '../../../components/WarningPopup';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const BackgroundVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission, canCreate } = usePermissions();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    vendor: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: bgFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateBgFilter,
    updatePage: updateBgPage,
    updateLimit: updateBgLimit,
    resetFilters: resetBgFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterStatus = bgFilters.status || 'all';
  const setFilterStatus = useCallback((status) => {
    updateBgFilter({ status });
  }, [updateBgFilter]);

  const filterVendor = bgFilters.vendor || 'all';
  const setFilterVendor = useCallback((vendor) => {
    updateBgFilter({ vendor });
  }, [updateBgFilter]);

  const dateRange = useMemo(() => ({
    startDate: bgFilters.startDate || '',
    endDate: bgFilters.endDate || ''
  }), [bgFilters.startDate, bgFilters.endDate]);

  const setDateRange = useCallback((range) => {
    updateBgFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateBgFilter]);

  const currentPage = bgFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateBgPage(page);
  }, [updateBgPage]);

  const itemsPerPage = bgFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateBgLimit(limit);
  }, [updateBgLimit]);
  const [isExporting, setIsExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteVerificationId, setDeleteVerificationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [pagination, setPagination] = useState({
    current: currentPage,
    pages: 1,
    total: 0,
    limit: itemsPerPage
  });
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Reset to page 1 when filters/search/date change
  useEffect(() => {
    setPagination((p) => ({ ...p, current: 1 }));
    setCurrentPage(1);
  }, [filterStatus, filterVendor, searchTerm, dateRange.startDate, dateRange.endDate, setCurrentPage]);

  // Load background verifications from API
  useEffect(() => {
    loadBackgroundVerifications();
    setSelectedRows(new Set());
  }, [filterStatus, filterVendor, searchTerm, currentPage, itemsPerPage, dateRange.startDate, dateRange.endDate]);

  const loadBackgroundVerifications = async () => {
    try {
      setLoading(true);
      setError(null);



      const params = {
        page: currentPage,
        limit: itemsPerPage,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        vendor: filterVendor !== 'all' ? filterVendor : undefined,
        search: searchTerm || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined
      };




      const result = await backgroundVerificationService.getBackgroundVerifications(params);

      if (result && result.responseCode === 200 && result.data) {

        setVerifications(result.data.verifications || []);
        if (result.data.pagination) {
          setPagination((prev) => ({
            current: result.data.pagination.current || result.data.pagination.page || prev.current,
            pages: result.data.pagination.pages || result.data.pagination.totalPages || prev.pages,
            total: result.data.pagination.total || result.data.pagination.totalItems || prev.total,
            limit: result.data.pagination.limit || result.data.pagination.itemsPerPage || prev.limit,
          }));
        }
      } else {

        setError('Failed to load background verifications');
        showError('Failed to load background verifications');
      }
    } catch (error) {

      setError('Error loading background verifications');
      showError('Error loading background verifications');
    } finally {
      setLoading(false);
    }
  };

  // Permission-based access control functions
  const canExportData = () => {
    return hasPermission('recruitment', 'read') || hasPermission('onboarding', 'read');
  };

  const canInitiateVerification = () => {
    return canCreate('recruitment') || canCreate('onboarding');
  };

  const canViewVerification = () => {
    return hasPermission('recruitment', 'read') || hasPermission('onboarding', 'read') || hasPermission('self_service', 'read');
  };

  const canEditVerification = () => {
    // Only super_admin and hr_manager can edit
    if (user?.role === 'hr_executive' || user?.role === 'employee') return false;
    return hasPermission('recruitment', 'update') || hasPermission('onboarding', 'update');
  };

  const canSendReminder = () => {
    return hasPermission('recruitment', 'update') || hasPermission('onboarding', 'update');
  };

  const canRefreshStatus = () => {
    return hasPermission('recruitment', 'update') || hasPermission('onboarding', 'update');
  };

  const canDeleteVerification = () => {
    return hasPermission('recruitment', 'delete') || hasPermission('onboarding', 'delete');
  };

  const mapVerificationsForExport = (data) => {
    return data.map(verification => ({
      'Candidate Name': verification.candidateName || '',
      'Email': verification.email || verification.candidateEmail || '',
      'Phone': verification.phone || '',
      'Candidate ID': verification.employeeId || '',
      'Position': verification.position || '',
      'Department': verification.department || '',
      'Vendor': typeof verification.vendor === 'string' ? verification.vendor : verification.vendor?.name || '',
      'Status': (verification.status || '').replace('_', ' '),
      'Priority': verification.priority || '',
      'Initiated Date': verification.initiatedDate ? formatDate(verification.initiatedDate, { includeTime: false }) : 'N/A',
      'Expected Completion Date': verification.expectedCompletionDate ? formatDate(verification.expectedCompletionDate, { includeTime: false }) : 'N/A',
      'Notes': verification.notes || ''
    }));
  };

  // Handler functions


  const handleExportCurrentPage = () => {
    const exportData = mapVerificationsForExport(verifications);
    exportToCSV(exportData, 'background_verification_page', {
      title: 'Background Verification List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (filterVendor !== "all") queryParams.append("vendor", filterVendor);

      const url = `${process.env.REACT_APP_API_BASE_URL}/background-verification/export?${queryParams.toString()}`;
      const filename = generateExportFilename('background_verification_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
    } catch (error) {

      setError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (filterVendor !== "all") queryParams.append("vendor", filterVendor);

      const url = `${process.env.REACT_APP_API_BASE_URL}/background-verification/export?${queryParams.toString()}`;
      const filename = generateExportFilename('background_verification_all');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
    } catch (error) {

      setError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleInitiateVerification = () => {
    navigate('/initiate_background_verification');
  };

  const handleViewVerification = (verificationId) => {
    navigate(`/view_background_verification/${verificationId}`);
  };

  const handleEditVerification = (verificationId) => {
    navigate(`/edit_background_verification/${verificationId}`);
  };



  const handleSendReminder = async (verificationId) => {
    try {


      const result = await backgroundVerificationService.sendReminder(verificationId);

      if (result && result.responseCode === 200) {

        showSuccess('Reminder sent successfully');

        // Reload verification data to get updated history
        await loadBackgroundVerifications();
      } else {

        showError(result?.responseMessage || 'Failed to send reminder');
      }
    } catch (error) {

      showError('Failed to send reminder');
    }
  };

  const handleRefreshStatus = async (verificationId) => {
    try {


      const result = await backgroundVerificationService.refreshStatus(verificationId);

      if (result && result.responseCode === 200) {

        showSuccess('Status refreshed successfully');

        // Reload verification data to get updated status
        await loadBackgroundVerifications();
      } else {

        showError(result?.responseMessage || 'Failed to refresh status');
      }
    } catch (error) {

      showError('Failed to refresh status');
    }
  };

  const handleDeleteVerification = (verificationId) => {
    setDeleteVerificationId(verificationId);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDeleteVerification = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteVerification = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        const idsToDelete = Array.from(selectedRows);
        let successCount = 0;
        let errorCount = 0;

        for (const id of idsToDelete) {
          try {
            const result = await backgroundVerificationService.deleteBackgroundVerification(id);
            const isSuccess = (result && result.responseCode === 200) ||
              (result && result.responseCode === 201) ||
              (result && result.success === true) ||
              (result && result.status === 'success');

            if (isSuccess) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {

            errorCount++;
          }
        }

        if (successCount > 0) {
          showSuccess(`${successCount} verifications deleted successfully`);
          setSelectedRows(new Set());
          loadBackgroundVerifications();
        }
        if (errorCount > 0) {
          showError(`Failed to delete ${errorCount} verifications`);
        }
      } else {
        if (!deleteVerificationId) return;
        const result = await backgroundVerificationService.deleteBackgroundVerification(deleteVerificationId);
        const isSuccess = (result && result.responseCode === 200) ||
          (result && result.responseCode === 201) ||
          (result && result.success === true) ||
          (result && result.status === 'success');

        if (isSuccess) {
          showSuccess('Background verification deleted successfully');
          loadBackgroundVerifications();
        } else {
          showError(result?.responseMessage || 'Failed to delete background verification');
        }
      }
      setShowDeleteModal(false);
      setDeleteVerificationId(null);
      setIsBulkDelete(false);
    } catch (error) {

      showError('An error occurred while deleting');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteVerification = () => {
    setShowDeleteModal(false);
    setDeleteVerificationId(null);
    setIsBulkDelete(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'initiated': return 'bg-blue-50 text-[#0F2942]';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed':
      case 'verified': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
      case 'initiated': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <RefreshCw className="h-4 w-4" />;
      case 'completed':
      case 'verified': return <CheckCircle className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const filteredVerifications = verifications;

  // Get current page verification IDs
  const currentPageVerificationIds = useMemo(
    () =>
      filteredVerifications
        .map((v) => {
          const raw = v?._id ?? v?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredVerifications]
  );

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      vendor: filterVendor !== 'all' ? filterVendor : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterStatus, filterVendor, dateRange.startDate, dateRange.endDate]);

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageVerificationIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'background-verification',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `background-verification-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      currentPageVerificationIds,
    ]
  );

  // Handle export for selected background verifications with format selection
  const handleBackgroundSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        showError('No selected verifications to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleBackgroundScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            showError('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageVerificationIds,
          });
        } else {
          return;
        }

        showSuccess('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      verifications.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageVerificationIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-6 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-[22px] font-bold text-[#0F2942] mb-0.5 leading-tight">Background Verification</h1>
            <p className="text-[12px] text-[#64748B]">Manage employee background verification process</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && canDeleteVerification() && (
            <PermissionGate permission="onboarding" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDeleteVerification}
                className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          {canExportData() && (
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected verifications`
                    : 'Export verifications'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleBackgroundSelectedFormatExport}
              />
            </div>
          )}
          {canInitiateVerification() && (
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleInitiateVerification}
              className="!bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white"
            >
              Initiate Verification
            </Button>
          )}
        </div>
      </div>

      {/* Filters (SaaS Toolbar Card) */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200/80">
        <div className="flex flex-wrap items-center gap-4 w-full">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search candidate name, Email ID or ID..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs font-medium bg-slate-50/50 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Status</option>
              {/* <option value="pending">Pending</option> */}
              <option value="initiated">Initiated</option>
              <option value="in_progress">In Progress</option>
              <option value="verified">Verified</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              inputClassName="input-field text-sm w-36 cursor-pointer outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              onChange={setDateRange}
            />
          </div>

          {/* Clear Filters */}
          {(searchTerm || filterStatus !== "all" || filterVendor !== "all" || dateRange.startDate || dateRange.endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={resetBgFilters}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Verification List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading verifications...</p>
              </div>
            </div>
          ) : filteredVerifications.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No verifications found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || filterStatus !== 'all' || dateRange.startDate || dateRange.endDate
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No background verifications have been initiated yet.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Table View */}
              <div className="overflow-x-auto border border-gray-100 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={verifications.length > 0 && verifications.every(v => selectedRows.has(v.id))}
                          onChange={() => {
                            const currentPageIds = verifications.map(v => v.id);
                            const isAllSelected = currentPageIds.every(id => selectedRows.has(id));

                            const newSelected = new Set(selectedRows);
                            if (isAllSelected) {
                              currentPageIds.forEach(id => newSelected.delete(id));
                            } else {
                              currentPageIds.forEach(id => newSelected.add(id));
                            }
                            setSelectedRows(newSelected);
                          }}
                          className="rounded border-gray-300 text-brand focus:ring-0 focus:outline-none focus-visible:outline-none"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Candidate</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Position & ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Vendor</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Dates</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Created At</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVerifications.map((verification) => (
                      <tr key={verification.id} className={`hover:bg-blue-50/30 transition-colors ${selectedRows.has(verification.id) ? 'bg-blue-50/30' : ''}`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(verification.id)}
                            onChange={() => {
                              const newSelectedRows = new Set(selectedRows);
                              if (newSelectedRows.has(verification.id)) {
                                newSelectedRows.delete(verification.id);
                              } else {
                                newSelectedRows.add(verification.id);
                              }
                              setSelectedRows(newSelectedRows);
                            }}
                            className="rounded border-gray-300 text-brand focus:ring-0 focus:outline-none focus-visible:outline-none h-4 w-4"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-gray-500" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-gray-900 capitalize">{verification.candidateName}</div>
                              <div className="text-xs text-gray-500">{verification.email || verification.candidateEmail || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 capitalize w-40 truncate">{verification.position}</div>
                          <div className="text-xs text-gray-500">ID: {verification.employeeId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 capitalize">{verification.vendor?.name || 'N/A'}</div>
                          {verification.vendor?.email && <div className="text-xs text-gray-500">{verification.vendor.email}</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(verification.status)}`}>
                            {getStatusIcon(verification.status)}
                            <span className="ml-1 capitalize">{verification.status.replace('_', ' ')}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs">
                            <span className="text-gray-500">Init: </span>
                            <span className="text-gray-900 font-medium">{verification.initiatedDate ? formatDate(verification.initiatedDate, { includeTime: false }) : 'N/A'}</span>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Exp: </span>
                            <span className="text-gray-900 font-medium">{verification.expectedCompletionDate ? formatDate(verification.expectedCompletionDate, { includeTime: false }) : 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-700 font-medium">
                            {verification.createdAt ? formatDate(verification.createdAt, { includeTime: true }) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {canViewVerification() && (
                              <button
                                onClick={() => handleViewVerification(verification.id)}
                                className="p-1.5 text-[#0F2942] hover:bg-blue-50 rounded-md transition-colors"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )}
                            {canEditVerification() && verification.status !== 'verified' && (
                              <button
                                onClick={() => handleEditVerification(verification.id)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {canSendReminder() && verification.status === 'pending' && (
                              <button
                                onClick={() => handleSendReminder(verification.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                title="Send Reminder"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                            )}
                            {canDeleteVerification() && (
                              <PermissionGate permission="onboarding" action="delete">
                                <button
                                  onClick={() => handleDeleteVerification(verification.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
            {/* Pagination Footer */}
            {!loading && (
              <PaginationControls
                border={false}
                currentPage={pagination.current}
                totalPages={pagination.pages}
                totalItems={pagination.total}
                limit={pagination.limit}
                hasNextPage={pagination.current < pagination.pages}
                hasPrevPage={pagination.current > 1}
                onPageChange={(newPage) => setCurrentPage(newPage)}
                onLimitChange={(newLimit) => setItemsPerPage(newLimit)}
                limitOptions={[10, 20, 50, 100]}
              />
            )}
          </div>
        )}
        </div>
      </div>



      {/* WarningPopup for Deletion */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={cancelDeleteVerification}
        onConfirm={confirmDeleteVerification}
        title={isBulkDelete ? "Delete Verifications" : "Delete Verification"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedRows.size} selected background verifications? This action cannot be undone.`
          : `Are you sure you want to delete this background verification? This action cannot be undone.`
        }
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Custom Alert Component */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Background Verification Data"
        showDateRange
        scopedFormatExport={{
          onExport: handleBackgroundScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div >
  );
};

export default BackgroundVerification;